import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { logError } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { openRazorpayCheckout } from "@/lib/razorpay";
import AIFirstWaitlistCard from "@/components/AIFirstWaitlistCard";
import {
    Check,
    Lightning,
    Sparkle,
    CircleNotch,
    Rocket,
    Crown,
    ArrowRight,
} from "@phosphor-icons/react";

const CURRENCY_SYMBOL = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };

const PLAN_ACCENTS = {
    starter: { icon: Sparkle, color: "text-cobalt", bg: "bg-cobalt/5" },
    growth: { icon: Rocket, color: "text-splash", bg: "bg-black text-white" },
    enterprise: { icon: Crown, color: "text-[#B45309]", bg: "bg-[#FEF3C7]/40" },
};

export default function Billing() {
    const { user } = useAuth();
    const isAdminOrRecruiter = ["admin", "recruiter"].includes(user?.role);
    const [params, setParams] = useSearchParams();
    const cancelledParam = params.get("cancelled");

    const [plans, setPlans] = useState([]);
    const [sub, setSub] = useState(null);
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyPlan, setBusyPlan] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            api.get("/subscription/plans"),
            api.get("/subscription/me"),
            api.get("/subscription/usage").catch(() => ({ data: null })),
        ])
            .then(([p, s, u]) => {
                setPlans(p.data.plans || []);
                setSub(s.data);
                setUsage(u.data);
            })
            .catch((e) => logError("Billing load failed:", e))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    // Legacy cancelled banner (Stripe redirect path) — no longer used with
    // Razorpay, but keep for people who bookmarked the old ?cancelled=1 URL.
    useEffect(() => {
        if (cancelledParam) {
            toast.info("Checkout cancelled — no charges made.");
            setParams({}, { replace: true });
        }
    }, [cancelledParam, setParams]);

    const upgrade = async (planId) => {
        if (!isAdminOrRecruiter) {
            toast.error("Only admins or recruiters can manage billing.");
            return;
        }
        setBusyPlan(planId);
        try {
            const { data: order } = await api.post("/subscription/checkout", {
                plan_id: planId,
                origin_url: window.location.origin,
            });
            const resp = await openRazorpayCheckout({
                order,
                name: "NaukriDaddy",
                description: `${order.plan_name} · 30 days`,
                prefill: { email: user?.email, name: user?.name },
            });
            // Verify on the server — activates the plan atomically.
            await api.post("/subscription/verify", resp);
            toast.success(`🎉 You're now on the ${planId.toUpperCase()} plan!`);
            // Refresh feature flags so newly-unlocked features appear without page reload.
            window.dispatchEvent(new CustomEvent("naukri:features-changed"));
            load();
        } catch (e) {
            if (e?.code === "dismissed") {
                toast.info("Checkout closed — no charges made.");
            } else if (e?.code === "failed") {
                toast.error(e.reason || "Payment failed. Please retry.");
            } else {
                toast.error(e.response?.data?.detail || e?.message || "Could not complete payment");
            }
        } finally {
            setBusyPlan(null);
        }
    };

    if (loading || !sub) {
        return <div className="p-10 text-[#525252]">Loading billing…</div>;
    }

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1400px] mx-auto" data-testid="billing-page">
            {/* Header */}
            <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                    Billing &amp; plans
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                    Pick a plan.
                </h1>
                <p className="text-[#525252] mt-3 max-w-xl">
                    Simple monthly pricing in INR. Cancel anytime — access remains
                    until your period ends.
                </p>
            </div>

            {/* Current subscription card */}
            <div
                data-testid="billing-current"
                className="mb-8 bg-white border border-[#E5E5E5] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
            >
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-1">
                        Currently on
                    </p>
                    <p className="font-heading font-black text-2xl sm:text-3xl tracking-tighter text-ink">
                        {(sub.tier || "free").toUpperCase()}
                    </p>
                    {sub.expires_at && (
                        <p className="text-xs text-[#525252] mt-1">
                            {sub.status === "active" ? "Renews" : "Expired"} on{" "}
                            {new Date(sub.expires_at).toLocaleDateString(undefined, {
                                month: "short", day: "numeric", year: "numeric",
                            })}
                        </p>
                    )}
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-[#525252]">
                    <LimitPill label="Clients" value={sub.limits?.clients} used={usage?.usage?.clients} />
                    <LimitPill label="Requirements" value={sub.limits?.requirements} used={usage?.usage?.requirements} />
                    <LimitPill label="Candidates" value={sub.limits?.candidates} used={usage?.usage?.candidates} />
                    <LimitPill label="AI matches" value={sub.limits?.ai_matches} used={usage?.usage?.ai_matches} />
                </div>
            </div>

            {/* Plan cards */}
            <div className="grid md:grid-cols-3 gap-4 sm:gap-5">
                {plans.map((plan) => {
                    const accent = PLAN_ACCENTS[plan.id] || PLAN_ACCENTS.starter;
                    const Icon = accent.icon;
                    const symbol = CURRENCY_SYMBOL[plan.currency] || plan.currency + " ";
                    const isCurrent = sub.tier === plan.id && sub.status === "active";
                    const featured = plan.featured;
                    return (
                        <div
                            key={plan.id}
                            data-testid={`plan-card-${plan.id}`}
                            className={`relative border p-6 flex flex-col ${
                                featured
                                    ? "bg-ink text-white border-ink"
                                    : "bg-white border-[#E5E5E5]"
                            }`}
                        >
                            {featured && (
                                <span className="absolute -top-3 left-6 bg-gradient-to-r from-splash to-cobalt text-ink text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1">
                                    Most popular
                                </span>
                            )}
                            <div className="flex items-center gap-2 mb-4">
                                <Icon size={20} weight="bold" className={featured ? "text-splash" : accent.color} />
                                <p className="font-heading font-black text-xl tracking-tighter">
                                    {plan.name}
                                </p>
                            </div>
                            <p className={`text-xs mb-5 ${featured ? "text-white/70" : "text-[#525252]"}`}>
                                {plan.tagline}
                            </p>
                            <div className="mb-5">
                                <span className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">
                                    {symbol}{plan.price.toLocaleString("en-IN")}
                                </span>
                                <span className={`text-xs ml-1 ${featured ? "text-white/60" : "text-[#A3A3A3]"}`}>
                                    /month
                                </span>
                            </div>
                            <ul className="space-y-2 mb-6 flex-1">
                                {plan.features.map((f, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm">
                                        <Check
                                            size={14}
                                            weight="bold"
                                            className={`mt-0.5 shrink-0 ${featured ? "text-splash" : "text-emerald-600"}`}
                                        />
                                        <span className={featured ? "text-white/90" : "text-[#262626]"}>
                                            {f}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            {isCurrent ? (
                                <button
                                    disabled
                                    data-testid={`plan-current-${plan.id}`}
                                    className="w-full px-4 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 bg-emerald-500/15 text-emerald-600 border border-emerald-500/30"
                                >
                                    <Check size={14} weight="bold" /> Current plan
                                </button>
                            ) : (
                                <button
                                    onClick={() => upgrade(plan.id)}
                                    disabled={busyPlan === plan.id || !isAdminOrRecruiter}
                                    data-testid={`plan-upgrade-${plan.id}`}
                                    className={`w-full px-4 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${
                                        featured
                                            ? "bg-gradient-to-r from-splash to-cobalt text-ink hover:opacity-90"
                                            : "bg-ink text-white hover:bg-black"
                                    }`}
                                >
                                    {busyPlan === plan.id ? (
                                        <><CircleNotch size={14} weight="bold" className="animate-spin" /> Opening…</>
                                    ) : (
                                        <><Lightning size={14} weight="bold" /> Upgrade <ArrowRight size={12} weight="bold" /></>
                                    )}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            <p className="text-[11px] text-[#A3A3A3] mt-6 text-center">
                Powered by Razorpay. UPI, cards & netbanking. Prices exclude GST where applicable.
            </p>

            {/* AI-First waitlist — signals demand for the V1.4 AI Interviewer feature */}
            <AIFirstWaitlistCard workspace={sub?.workspace || {}} user={user} />
        </div>
    );
}

function LimitPill({ label, value, used }) {
    const isUnlimited = value === -1;
    const display = isUnlimited ? "∞" : (value ?? "—");
    const hasUsage = typeof used === "number" && value !== undefined && value !== null;
    const nearLimit = hasUsage && !isUnlimited && value > 0 && used / value >= 0.8;
    return (
        <span
            data-testid={`limit-pill-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`inline-flex items-center gap-1.5 border px-2.5 py-1.5 ${
                nearLimit ? "bg-[#FEF3C7] border-[#F59E0B]/40" : "bg-[#FAFAFA] border-[#E5E5E5]"
            }`}
        >
            {hasUsage && !isUnlimited && (
                <span className={`font-heading font-black text-sm tracking-tighter ${nearLimit ? "text-[#B45309]" : "text-[#525252]"}`}>
                    {used}/
                </span>
            )}
            <span className="font-heading font-black text-sm tracking-tighter text-ink">{display}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">{label}</span>
        </span>
    );
}
